import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useAgentsAccess } from 'ai-studio-lib/features/ai-access-controls/hooks/useAgentsAccess';
import { getAiSettingsUrl } from 'ai-studio-lib/shared/utils/urls';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { gate } from 'hub-http/gates';
import { useCallback, useMemo, useState } from 'react';
import { useCreateInstanceFromTemplateByGlobalIdMutation, useGetInstancesByGlobalIdQuery } from '../../__generated__/chirp/com/hubspot/ai/agent/rpc/AiAgentInstancesChirpServiceV2.hooks';
import { useInstallCustomAssistantTemplateFromGlobalIdMutation, useLookupUserInstalledAssistantByGlobalIdQuery } from '../../__generated__/chirp/com/hubspot/copilot/rpc/CustomAssistantsService.hooks';
import { useGetInstallCompatibilityQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc.hooks';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { JoinPublicBetaModal } from '../install/JoinPublicBetaModal';
import { getAgentStudioConfigurationUrl, getAgentStudioUrl, getBreezeStudioUrl } from '../utils/DetailsPageUrls';
import { trackCustomMarketplaceOfferingBehavioralEvent } from '../utils/TrackingUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BREEZE_AGENT_EDIT = 'BREEZE_AGENT_EDIT';
const BREEZE_AGENT_ACCESS = 'BREEZE_AGENT_ACCESS';
function BreezeStudioLink({
  agentType,
  children
}) {
  return /*#__PURE__*/_jsx(Link, {
    external: true,
    href: getBreezeStudioUrl(agentType),
    children: children
  });
}
export function AgentCTAButton({
  agentType,
  betaDetails,
  installUrl,
  onInstallComplete,
  onInstallError,
  sourceId
}) {
  var _installCompatibility6;
  const {
    appContext,
    listingId,
    name: listingName,
    published,
    isEmbedded,
    trackInteraction,
    trackInstall
  } = useDetailsPageContext();
  const {
    userInfo
  } = useDetailsPageContext();
  const {
    hasAgentsAccess
  } = useAgentsAccess();
  const isPublicBeta = (betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.betaStage) === 'PUBLIC_BETA';
  const [isPublicBetaModalOpen, setIsPublicBetaModalOpen] = useState(false);
  const hasJoinedPublicBeta = useMemo(() => {
    if (isPublicBeta && betaDetails !== null && betaDetails !== void 0 && betaDetails.gateName) {
      var _userInfo$gates;
      // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
      return userInfo === null || userInfo === void 0 || (_userInfo$gates = userInfo.gates) === null || _userInfo$gates === void 0 ? void 0 : _userInfo$gates.includes(gate(betaDetails.gateName));
    }
    return true;
  }, [isPublicBeta, userInfo, betaDetails]);
  const {
    data: installCompatibilityData,
    loading: installCompatibilityLoading
  } = useGetInstallCompatibilityQuery({
    skip: appContext !== 'app',
    variables: {
      listingId: listingId !== null && listingId !== void 0 ? listingId : 0,
      published
    }
  });
  const launchCopilotWithCustomCopilot = useSendOnCopilotChannel('LAUNCH_WITH_CUSTOM_COPILOT');
  const {
    installed,
    userInstallable,
    portalInstallable
  } = useMemo(() => {
    var _installCompatibility, _installCompatibility2, _installCompatibility3, _installCompatibility4, _installCompatibility5;
    const installCompatibility = installCompatibilityData === null || installCompatibilityData === void 0 ? void 0 : installCompatibilityData.productInstallCompatibility;
    return {
      installed: (_installCompatibility = installCompatibility === null || installCompatibility === void 0 ? void 0 : installCompatibility.installed) !== null && _installCompatibility !== void 0 ? _installCompatibility : false,
      userInstallable: (_installCompatibility2 = installCompatibility === null || installCompatibility === void 0 || (_installCompatibility3 = installCompatibility.installability) === null || _installCompatibility3 === void 0 ? void 0 : _installCompatibility3.userInstallable) !== null && _installCompatibility2 !== void 0 ? _installCompatibility2 : true,
      //TODO: fallback to false once agent instance reliability is improved
      portalInstallable: (_installCompatibility4 = installCompatibility === null || installCompatibility === void 0 || (_installCompatibility5 = installCompatibility.installability) === null || _installCompatibility5 === void 0 ? void 0 : _installCompatibility5.portalInstallable) !== null && _installCompatibility4 !== void 0 ? _installCompatibility4 : true //TODO: fallback to false once agent instance reliability is improved
    };
  }, [installCompatibilityData]);
  const uninstallableReason = installCompatibilityData === null || installCompatibilityData === void 0 || (_installCompatibility6 = installCompatibilityData.productInstallCompatibility) === null || _installCompatibility6 === void 0 || (_installCompatibility6 = _installCompatibility6.installability) === null || _installCompatibility6 === void 0 ? void 0 : _installCompatibility6.uninstallableReason;
  const missingBreezeAgentAccess = uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.missingUserScopes.includes(BREEZE_AGENT_ACCESS);
  const uninstallableReasonText = useMemo(() => {
    if (uninstallableReason !== null && uninstallableReason !== void 0 && uninstallableReason.missingUserScopes.includes(BREEZE_AGENT_EDIT) || missingBreezeAgentAccess) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "detailsPage.productDetailsCard.cta.uninstallableReasons.breezeAgentEdit"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.uninstallableReasons.cannotInstallAgent"
    });
  }, [missingBreezeAgentAccess, uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.missingUserScopes]);
  const {
    data: agentInstancesData,
    loading: agentInstanceLoading,
    refetch: agentInstancesRefetch
  } = useGetInstancesByGlobalIdQuery({
    skip: !sourceId || appContext !== 'app' || agentType !== 'STUDIO',
    variables: {
      globalId: sourceId ? parseInt(sourceId, 10) : 0,
      globalIdString: sourceId !== null && sourceId !== void 0 ? sourceId : ''
    }
  });
  const agentInstance = useMemo(() => {
    if (!agentInstancesData || agentInstancesData.instances.length === 0) {
      return undefined;
    }
    return agentInstancesData.instances[0];
  }, [agentInstancesData]);
  const {
    data: installedAssistantData,
    refetch: installedAssistantRefetch
  } = useLookupUserInstalledAssistantByGlobalIdQuery({
    variables: {
      globalId: sourceId || ''
    },
    skip: !sourceId || appContext !== 'app' || agentType !== 'ASSISTANT'
  });
  const installedAssistant = useMemo(() => {
    if (!installedAssistantData) {
      return undefined;
    }
    return installedAssistantData.installedAssistant;
  }, [installedAssistantData]);
  const [createAgentInstance, {
    loading: createAgentInstanceLoading
  }] = useCreateInstanceFromTemplateByGlobalIdMutation({
    variables: {
      globalId: sourceId ? parseInt(sourceId, 10) : 0,
      globalIdString: sourceId !== null && sourceId !== void 0 ? sourceId : ''
    }
  });
  const [installAssistantFromGlobalId, {
    loading: installAssistantFromGlobalIdLoading
  }] = useInstallCustomAssistantTemplateFromGlobalIdMutation({
    variables: {
      templateGlobalId: sourceId || ''
    }
  });
  const handleAgentInstalledSuccessfully = useCallback(instanceId => {
    if (agentType === 'ASSISTANT') {
      // Refetch assistant install data after successful install
      installedAssistantRefetch().catch(error => {
        console.error(error);
      });
    }
    FloatingAlertStore.addAlert({
      maxInlineSize: 'unset',
      sticky: true,
      titleText: listingName ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentInstalledSuccessAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        },
        options: {
          linkProps: {
            agentType
          },
          listingName
        }
      }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.genericAgentInstalledSuccessAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        },
        options: {
          linkProps: {
            agentType
          }
        }
      }),
      type: 'success',
      message: instanceId && /*#__PURE__*/_jsx(ButtonWrapper, {
        children: /*#__PURE__*/_jsx(Button, {
          href: getAgentStudioConfigurationUrl(instanceId, agentType),
          onClick: () => trackInteraction({
            action: 'clicked open cta button',
            subscreen: 'install success alert'
          }),
          external: true,
          size: "small",
          use: "tertiary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.configure"
          })
        })
      })
    });
    agentInstancesRefetch().catch(error => {
      console.error('Error refetching agent data:', error);
    });
    if (onInstallComplete) {
      onInstallComplete();
    }
  }, [agentInstancesRefetch, agentType, installedAssistantRefetch, listingName, onInstallComplete, trackInteraction]);
  const handleAgentInstallFailed = useCallback(error => {
    console.error('Error installing agent:', error);
    FloatingAlertStore.addAlert({
      maxInlineSize: 'unset',
      titleText: listingName ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentInstalledFailedAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        },
        options: {
          listingName
        }
      }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.genericAgentInstalledFailedAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        }
      }),
      type: 'danger'
    });
    if (onInstallError) {
      onInstallError();
    }
  }, [listingName, onInstallError]);
  const handleInstallAgent = () => {
    if (agentType === 'ASSISTANT') {
      installAssistantFromGlobalId().then(response => {
        if (response !== null && response !== void 0 && response.data) {
          var _response$data$result;
          handleAgentInstalledSuccessfully((_response$data$result = response.data.result) === null || _response$data$result === void 0 || (_response$data$result = _response$data$result.customAssistant) === null || _response$data$result === void 0 ? void 0 : _response$data$result.id);
        }
      }).catch(error => handleAgentInstallFailed(error));
    } else {
      createAgentInstance().then(response => {
        if (response !== null && response !== void 0 && response.data) {
          var _response$data$result2;
          handleAgentInstalledSuccessfully((_response$data$result2 = response.data.result) === null || _response$data$result2 === void 0 || (_response$data$result2 = _response$data$result2.agentInstance) === null || _response$data$result2 === void 0 ? void 0 : _response$data$result2.id);
        }
      }).catch(error => {
        handleAgentInstallFailed(error);
      });
    }
  };

  // Render a loading state button to prevent vulnerabilities that can be caused by assuming that a user can install
  if (createAgentInstanceLoading || installCompatibilityLoading || agentInstanceLoading || installAssistantFromGlobalIdLoading) {
    return /*#__PURE__*/_jsx(LoadingButton, {
      size: "medium",
      use: "primary",
      disabled: true,
      loading: true,
      role: "button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.AGENT"
      })
    });
  }

  // Render an "Open" button if the agent is installed except for when the user does not have agent studio access
  if ((installed || agentInstance !== null && agentInstance !== void 0 && agentInstance.id || installedAssistant !== null && installedAssistant !== void 0 && installedAssistant.id) && !missingBreezeAgentAccess) {
    var _agentInstance$id;
    return (
      /*#__PURE__*/
      // TODO: some handling might be needed for an agent that does not have a studio URL (Customer Agent)
      _jsx(Button, {
        use: "primary",
        href: agentType !== 'ASSISTANT' ? getAgentStudioUrl((_agentInstance$id = agentInstance === null || agentInstance === void 0 ? void 0 : agentInstance.id) !== null && _agentInstance$id !== void 0 ? _agentInstance$id : 0) : undefined,
        external: isEmbedded,
        onClick: () => {
          trackInteraction({
            action: 'clicked open cta button'
          });
          if (agentType === 'ASSISTANT') {
            launchCopilotWithCustomCopilot({
              customAssistantType: 'chatAgent',
              customAssistant: {
                id: installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.id,
                name: (installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.name) || '',
                welcomeMessage: installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.description,
                iconUrl: installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.icon,
                type: 'chatAgent'
              }
            });
          }
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.open"
        })
      })
    );
  }

  // Render a disabled button if the agent is not installable
  if (!userInstallable || !portalInstallable || !hasAgentsAccess) {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: !hasAgentsAccess ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.cta.updateAgentSettings_jsx",
        elements: {
          Link: OpenAISettingsLink
        }
      }) : uninstallableReasonText,
      children: /*#__PURE__*/_jsx(Button, {
        use: "primary",
        disabled: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.AGENT"
        })
      })
    });
  }

  // Render a "Join beta and add" button if the agent is installable but the user has not joined the public beta
  if (isPublicBeta && !hasJoinedPublicBeta) {
    var _betaDetails$rolloutI;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: () => {
          trackInteraction({
            action: 'clicked join beta and add cta button'
          });
          setIsPublicBetaModalOpen(true);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.joinBetaAndAdd"
        })
      }), isPublicBetaModalOpen && /*#__PURE__*/_jsx(JoinPublicBetaModal, {
        title: listingName,
        onClose: () => setIsPublicBetaModalOpen(false),
        rolloutId: (_betaDetails$rolloutI = betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.rolloutId) !== null && _betaDetails$rolloutI !== void 0 ? _betaDetails$rolloutI : 0,
        onSuccessfulJoin: handleInstallAgent
      })]
    });
  }

  // Render an external link button for off-platform agents
  if (agentType === 'OFF_PLATFORM') {
    return /*#__PURE__*/_jsx(Button, {
      href: installUrl !== null && installUrl !== void 0 ? installUrl : '',
      external: true,
      onClick: () => {
        trackInteraction({
          action: 'clicked off platform Install button'
        });
        trackInstall({});
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.AGENT"
      })
    });
  }

  // Render standard button for all other agents
  return /*#__PURE__*/_jsx(Button, {
    use: "primary",
    onClick: () => {
      trackInstall({});
      trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
      handleInstallAgent();
    },
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.install.AGENT"
    })
  });
}
function OpenAISettingsLink({
  children
}) {
  return /*#__PURE__*/_jsx(Link, {
    external: true,
    href: getAiSettingsUrl(),
    use: "on-dark",
    children: children
  });
}